import Konva from "konva";
import { solidOf } from "../../lib/position-tone.js";
import type { Shape, Stroke, TextAnnotation } from "../canvas-kit/drawing/types.js";
import { makeKonvaRenderer, type PageRenderer } from "../canvas-kit/export/index.js";
import {
  CARD_PALETTE,
  type CardWidget,
  type PlayerWidget,
  STICKY_PALETTE,
  type StickyNote,
  type WhiteboardSnapshot,
  type Widget,
} from "./snapshot.js";

/**
 * Build Konva nodes for one whiteboard page. Background fill + optional
 * dot grid are drawn first, then the user-authored primitives in the same
 * order the live editor uses (strokes → shapes → texts).
 *
 * Coordinates are in board units; the off-screen stage created by
 * `makeKonvaRenderer` scales them to the paper's pixel resolution.
 */
function buildWhiteboardNodes({
  snapshot,
  layer,
}: {
  snapshot: WhiteboardSnapshot;
  page: unknown;
  layer: Konva.Layer;
}) {
  const isDark = snapshot.bg === "dark";
  const bgFill = isDark ? "#101418" : "#f5f5f0";
  const gridColor = isDark ? "rgba(255,255,255,0.18)" : "rgba(0,0,0,0.18)";

  // Full-board background — covers the page rect even when the snapshot
  // didn't carry an explicit background colour.
  layer.add(
    new Konva.Rect({
      x: -1e6,
      y: -1e6,
      width: 2e6,
      height: 2e6,
      fill: bgFill,
      listening: false,
    }),
  );

  if (snapshot.grid.enabled) {
    addGrid(layer, snapshot.grid.size, gridColor);
  }
  for (const s of snapshot.strokes) addStroke(layer, s);
  for (const s of snapshot.shapes) addShape(layer, s);
  for (const t of snapshot.texts) addText(layer, t);
  for (const sk of snapshot.stickies) addSticky(layer, sk);
  for (const w of snapshot.widgets) addWidget(layer, w);
}

export const whiteboardRenderer: PageRenderer<WhiteboardSnapshot> =
  makeKonvaRenderer(buildWhiteboardNodes);

/* ─── primitives ─────────────────────────────────────────────── */

function addStroke(layer: Konva.Layer, st: Stroke) {
  layer.add(
    new Konva.Line({
      points: st.points,
      stroke: st.color,
      strokeWidth: st.width,
      tension: 0.35,
      lineCap: "round",
      lineJoin: "round",
      dash: st.dashed ? [st.width * 3, st.width * 2] : undefined,
      listening: false,
    }),
  );
}

function addShape(layer: Konva.Layer, sh: Shape) {
  const dash = sh.dashed ? [sh.width * 3, sh.width * 2] : undefined;
  switch (sh.kind) {
    case "line":
      layer.add(
        new Konva.Line({
          x: sh.x,
          y: sh.y,
          points: [0, 0, sh.w, sh.h],
          stroke: sh.color,
          strokeWidth: sh.width,
          dash,
          lineCap: "round",
          listening: false,
        }),
      );
      break;
    case "arrow":
      layer.add(
        new Konva.Arrow({
          x: sh.x,
          y: sh.y,
          points: [0, 0, sh.w, sh.h],
          stroke: sh.color,
          fill: sh.color,
          strokeWidth: sh.width,
          dash,
          pointerLength: sh.width * 4,
          pointerWidth: sh.width * 4,
          listening: false,
        }),
      );
      break;
    case "rect":
      layer.add(
        new Konva.Rect({
          x: sh.x,
          y: sh.y,
          width: sh.w,
          height: sh.h,
          stroke: sh.color,
          strokeWidth: sh.width,
          dash,
          cornerRadius: 6,
          listening: false,
        }),
      );
      break;
    case "circle":
      layer.add(
        new Konva.Circle({
          x: sh.x,
          y: sh.y,
          radius: Math.max(Math.abs(sh.w), 1),
          stroke: sh.color,
          strokeWidth: sh.width,
          dash,
          listening: false,
        }),
      );
      break;
  }
}

function addText(layer: Konva.Layer, tx: TextAnnotation) {
  layer.add(
    new Konva.Text({
      x: tx.x,
      y: tx.y,
      text: tx.value,
      fontSize: tx.fontSize,
      fontStyle: "700",
      fill: tx.color,
      listening: false,
    }),
  );
}

function addSticky(layer: Konva.Layer, sk: StickyNote) {
  const palette = STICKY_PALETTE[sk.color];
  layer.add(
    new Konva.Rect({
      x: sk.x + 6,
      y: sk.y + 8,
      width: sk.w,
      height: sk.h,
      fill: "rgba(0,0,0,0.28)",
      cornerRadius: 6,
      listening: false,
    }),
  );
  layer.add(
    new Konva.Rect({
      x: sk.x,
      y: sk.y,
      width: sk.w,
      height: sk.h,
      fill: palette.paper,
      stroke: palette.stroke,
      strokeWidth: 1.5,
      cornerRadius: 6,
      listening: false,
    }),
  );
  if (sk.value) {
    layer.add(
      new Konva.Text({
        x: sk.x + 12,
        y: sk.y + 12,
        width: sk.w - 24,
        height: sk.h - 24,
        text: sk.value,
        fontSize: sk.fontSize,
        fontStyle: "600",
        fill: palette.ink,
        wrap: "word",
        listening: false,
      }),
    );
  }
}

function addGrid(layer: Konva.Layer, size: number, color: string) {
  // Bounded dot grid centred on the visible page region. We use the
  // layer's clip-region (set by the stage in `render-konva`) implicitly:
  // dots outside the page board rect are simply not visible in the
  // rasterised output.
  const radius = 1.5;
  const half = 3000; // covers any sensible page rectangle
  for (let y = -half; y <= half; y += size) {
    for (let x = -half; x <= half; x += size) {
      layer.add(
        new Konva.Circle({
          x,
          y,
          radius,
          fill: color,
          listening: false,
          perfectDrawEnabled: false,
        }),
      );
    }
  }
}

function addWidget(layer: Konva.Layer, w: Widget) {
  if (w.kind === "player") addPlayerWidget(layer, w);
  else addCardWidget(layer, w);
}

function addPlayerWidget(layer: Konva.Layer, w: PlayerWidget) {
  const tone = solidOf(w.position);
  const radius = Math.min(w.h, w.w) * 0.42;
  const cx = w.x + radius + 12;
  const cy = w.y + w.h / 2;
  layer.add(
    new Konva.Rect({
      x: w.x + 6,
      y: w.y + 8,
      width: w.w,
      height: w.h,
      fill: "rgba(0,0,0,0.35)",
      cornerRadius: 14,
      listening: false,
    }),
  );
  layer.add(
    new Konva.Rect({
      x: w.x,
      y: w.y,
      width: w.w,
      height: w.h,
      fill: "#0f1620",
      stroke: "rgba(255,255,255,0.18)",
      strokeWidth: 1.5,
      cornerRadius: 14,
      listening: false,
    }),
  );
  layer.add(
    new Konva.Circle({
      x: cx,
      y: cy,
      radius,
      fill: tone.fill,
      stroke: tone.stroke,
      strokeWidth: 3,
      listening: false,
    }),
  );
  layer.add(
    new Konva.Text({
      x: cx - radius,
      y: cy - radius,
      width: radius * 2,
      height: radius * 2,
      text: w.shirt != null ? String(w.shirt) : initialsOf(w.name),
      fontSize: radius * 0.95,
      fontStyle: "800",
      fill: tone.fg,
      align: "center",
      verticalAlign: "middle",
      listening: false,
    }),
  );
  layer.add(
    new Konva.Text({
      x: cx + radius + 14,
      y: w.y + 18,
      width: w.w - (cx + radius + 14 - w.x) - 12,
      text: w.name || "Unbenannt",
      fontSize: 22,
      fontStyle: "700",
      fill: "#f5f8ff",
      ellipsis: true,
      wrap: "none",
      listening: false,
    }),
  );
  layer.add(
    new Konva.Text({
      x: cx + radius + 14,
      y: w.y + 46,
      width: w.w - (cx + radius + 14 - w.x) - 12,
      text: w.position ?? "—",
      fontSize: 16,
      fontStyle: "600",
      fill: "rgba(245,248,255,0.65)",
      listening: false,
    }),
  );
}

function addCardWidget(layer: Konva.Layer, w: CardWidget) {
  const palette = CARD_PALETTE[w.accent];
  layer.add(
    new Konva.Rect({
      x: w.x + 6,
      y: w.y + 8,
      width: w.w,
      height: w.h,
      fill: "rgba(0,0,0,0.35)",
      cornerRadius: 14,
      listening: false,
    }),
  );
  layer.add(
    new Konva.Rect({
      x: w.x,
      y: w.y,
      width: w.w,
      height: w.h,
      fill: palette.paper,
      cornerRadius: 14,
      listening: false,
    }),
  );
  layer.add(
    new Konva.Rect({
      x: w.x - 2,
      y: w.y,
      width: 9,
      height: w.h,
      fill: palette.stroke,
      cornerRadius: [14, 0, 0, 14],
      listening: false,
    }),
  );
  layer.add(
    new Konva.Text({
      x: w.x + 18,
      y: w.y + 14,
      width: w.w - 32,
      text: w.title || "Übungsblock",
      fontSize: 22,
      fontStyle: "800",
      fill: palette.ink,
      wrap: "word",
      listening: false,
    }),
  );
  if (w.body) {
    layer.add(
      new Konva.Text({
        x: w.x + 18,
        y: w.y + 48,
        width: w.w - 32,
        height: w.h - 60,
        text: w.body,
        fontSize: 16,
        fontStyle: "500",
        fill: palette.subtle,
        wrap: "word",
        listening: false,
      }),
    );
  }
}

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase() ?? "").join("") || "?";
}
